import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button } from '@appcues/sonar';
import { fetchSurveys, selectResponse } from 'entities/surveys';
import { asPromised } from 'utils/as-promised';
import { buildURI } from 'utils/CSV';
import { selectFlow } from 'reducers/account/flows';

const DownloadLink = styled.a``;

const transformRows = (answers, flow) =>
  answers.map(({ fieldAnswers, userId, timestamp, userProfileAttributes }) => ({
    timestamp,
    'attributes.flowId': flow?.id,
    'attributes.flowName': flow?.name,
    'attributes.fullname': userProfileAttributes?.fullname,
    userId,
    'attributes.email': userProfileAttributes?.email,
    'attributes.userEmail': userProfileAttributes?.userEmail,
    ...fieldAnswers.reduce(
      (acc, { fieldId, value }) => ({
        ...acc,
        [fieldId.replaceAll("'", '')]: value,
      }),
      {}
    ),
  }));

function slugify(text, separator = '-') {
  return text
    .toString()
    .toLowerCase()
    .replace(/[^\s\w-]/g, '') // Remove non-word characters except spaces and hyphens
    .replace(/\s+/g, separator) // Replace spaces with separator
    .replace(/-{2,}/g, separator) // Replace consecutive separators with one separator
    .replace(/^-+|-+$/g, ''); // Remove leading and trailing separators
}

const DownloadSurveyButton = ({
  flowId,
  flow,
  onLoad,
  surveys,
  start,
  end,
  onClick,
}) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const isValidDateRange = start && end && start < end;

    const load = async () => {
      setLoading(true);
      await onLoad?.();
      setLoading(false);
    };

    if (isValidDateRange) {
      load();
    }
  }, [start, end]);

  const columns = useMemo(
    () => [
      { key: 'timestamp', label: 'timestamp' },
      { key: 'attributes.flowId', label: 'attributes.flowId' },
      { key: 'attributes.flowName', label: 'attributes.flowName' },
      { key: 'attributes.fullname', label: 'attributes.fullname' },
      { key: 'userId', label: 'user_id' },
      { key: 'attributes.email', label: 'attributes.email' },
      { key: 'attributes.userEmail', label: 'attributes.userEmail' },
      ...(surveys?.labels || []).map(
        ({ fieldId: key, label, stepChildId, stepChildNumber }) => ({
          key,
          label: `field_-${slugify(label)}-__${stepChildNumber || stepChildId}`,
        })
      ),
    ],
    [surveys?.labels]
  );

  const transformedRows = useMemo(
    () => transformRows(surveys?.answers || [], flow),
    [surveys?.answers]
  );

  return (
    <DownloadLink
      download={flowId}
      rel="noreferrer"
      target="_blank"
      href={buildURI(transformedRows, undefined, columns)}
      onClick={onClick}
    >
      <Button loading={loading} disabled={!start || !end}>
        Download CSV
      </Button>
    </DownloadLink>
  );
};

const mapStateToProps = (state, { flowId: id }) => ({
  flow: selectFlow(state, id),
  surveys: selectResponse(state, id),
});

const mapDispatchToProps = (dispatch, { flowId: id, start, end }) => ({
  onLoad: () =>
    asPromised(
      dispatch,
      fetchSurveys(id, start, end, ['email', 'userEmail', 'fullname'])
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DownloadSurveyButton);
